import { action, computed, makeObservable, observable } from 'mobx';

import { Store } from '../../store/store';
import { StoreNode } from '../../store';
import Routes, { BackRoutes, type LocationDescriptor } from '../../routes';
import {
  GuidedTrainingDomSync,
  getGuidedTrainingCoachPosition,
  getGuidedTrainingStorageKey,
  getGuidedTrainingTargetRect,
  type GuidedTrainingCoachPosition,
  type GuidedTrainingRect,
} from '../../components/guidedTraining/guidedTrainingCore';

type Step = {
  title: string;
  description: string;
  targetId?: string;
  finalAction?: boolean;
};

type CoachPosition = GuidedTrainingCoachPosition;

type OpenPayload = {
  showUploadButtons: boolean;
  showTrainButton: boolean;
  teamId: string;
};

type Progress = {
  teamId: string;
  currentStepIndex: number;
  showUploadButtons: boolean;
  showTrainButton: boolean;
};

const progressStoragePrefix = 'guidedTraining.account';
const getTargetRect = (targetId?: string): GuidedTrainingRect | null =>
  getGuidedTrainingTargetRect(targetId, {
    padding: id => id === 'team-dashboard-filters' ? 5 : 8,
  });

const getCoachPosition = (rect: GuidedTrainingRect | null): CoachPosition =>
  getGuidedTrainingCoachPosition(rect, {
    maxWidth: 420,
    height: 190,
    defaultTopRatio: 0.24,
    defaultTopMin: 120,
    preferRight: false,
  });

export class AccountGuidedTrainingState
  extends StoreNode {

  readonly nodeType = 'AccountGuidedTraining';

  constructor(store: Store) {
    super(store);
    makeObservable(this);
    this.domSync = new GuidedTrainingDomSync({
      syncPosition: this.syncPosition,
      onKeyDown: this.handleKeyDown,
    });
  }

  @observable isVisible = false;
  @observable isArmed = false;
  @observable currentStepIndex = 0;
  @observable showUploadButtons = false;
  @observable showTrainButton = false;
  @observable teamId: string | null = null;
  @observable.ref targetRect: GuidedTrainingRect | null = null;
  @observable.ref coachPosition: CoachPosition = { top: 120, left: 16, width: 420 };

  private readonly domSync: GuidedTrainingDomSync;

  @computed
  get userName(): string {
    return this.store.user?.firstName || this.store.user?.name || 'there';
  }

  @computed
  get uploadRoute(): LocationDescriptor | null {
    if (!this.teamId) return null;

    return BackRoutes[Routes.teamUpload()].route({
      currentPath: Routes.teamDashboard(this.teamId),
      teamId: this.teamId,
    });
  }

  private get progressStorageKey(): string {
    return getGuidedTrainingStorageKey(this.store, progressStoragePrefix);
  }

  @computed
  get steps(): Step[] {
    return [
      {
        title: `Welcome to CLIPr, ${this.userName}!`,
        description: `Let's take 60 seconds to explore your workspace and the tools you'll use most often.`,
      },
      ...(this.showUploadButtons ? [
        {
          title: 'Upload documents',
          description: 'Upload text files, transcripts, case notes, or supporting documents here.',
          targetId: 'team-upload-doc',
        },
        {
          title: 'Upload media',
          description: 'Import audio, video, or body-worn camera footage here.',
          targetId: 'team-upload-media',
        },
      ] : []),
      ...(this.showTrainButton ? [
        {
          title: 'Create agency AI',
          description: 'Create AI models from approved agency media for CLIPr Admin and right hand.',
          targetId: 'team-train',
        },
      ] : []),
      {
        title: 'Search your Library',
        description: 'Use the search bar above to locate a recording, report, or document by name.',
        targetId: 'team-dashboard-search',
      },
      {
        title: 'Filter and sort assets',
        description: 'Use Type, Upload Date, and Length separately from Search to narrow and order your results.',
        targetId: 'team-dashboard-filters',
      },
      {
        title: this.shouldStartAdminTraining
          ? this.showUploadButtons ? "You're ready for Upload & Generate" : "Admin Setup is ready when you are"
          : this.showUploadButtons ? "You're ready for Upload & Generate" : "You're ready to use your Library",
        description: this.shouldStartAdminTraining
          ? this.showUploadButtons
            ? `Next, upload a real practice video. When you're ready for Admin Setup, open the Gear icon on this Library.`
            : `Open the Gear icon on this Library when you're ready to manage Library settings and custom report formats.`
          : this.showUploadButtons
          ? `Next, you'll upload a real practice video into the correct named Library and generate a report.`
          : `You're ready to search, filter, and review approved Library assets.`,
        finalAction: true,
      },
    ];
  }

  @computed
  get currentStep(): Step | null {
    return this.steps[this.currentStepIndex] || null;
  }

  @computed
  get totalSteps(): number {
    return this.steps.length;
  }

  @computed
  get isWelcomeStep(): boolean {
    return this.currentStepIndex === 0;
  }

  @computed
  get primaryActionLabel(): string {
    if (!this.currentStep?.finalAction)
      return 'Next';

    return this.showUploadButtons ? 'Start Upload & Generate' : 'Finish';
  }

  @computed
  get shouldStartAdminTraining(): boolean {
    return this.store.adminGuidedTraining.canStart(this.showTrainButton, this.teamId);
  }

  @action
  open(payload: OpenPayload) {
    this.domSync.unbindListeners();
    this.showUploadButtons = payload.showUploadButtons;
    this.showTrainButton = payload.showTrainButton;
    this.teamId = payload.teamId;
    this.currentStepIndex = 0;
    this.isArmed = true;
    this.isVisible = true;
    this.saveProgress();
    this.domSync.bindListeners();
    this.domSync.schedulePositionSync();
  }

  @action
  resume(payload: OpenPayload): boolean {
    const progress = this.loadProgress();
    if (!progress || progress.teamId !== payload.teamId)
      return false;

    this.domSync.unbindListeners();
    this.showUploadButtons = payload.showUploadButtons;
    this.showTrainButton = payload.showTrainButton;
    this.teamId = payload.teamId;
    this.currentStepIndex = clamp(progress.currentStepIndex, 0, this.steps.length - 1);
    this.isArmed = true;
    this.isVisible = true;
    this.saveProgress();
    this.domSync.bindListeners();
    this.domSync.schedulePositionSync();

    return true;
  }

  @action
  pause = () => {
    if (this.isArmed)
      this.saveProgress();

    this.isVisible = false;
    this.targetRect = null;
    this.domSync.unbindListeners();
    this.domSync.cancelPositionSync();
  }

  @action
  complete = () => {
    this.store.storage.removeLocal(this.progressStorageKey);
    this.reset();
  }

  @action
  close = () => {
    this.complete();
  }

  @action
  private reset = () => {
    this.isArmed = false;
    this.isVisible = false;
    this.currentStepIndex = 0;
    this.targetRect = null;
    this.teamId = null;
    this.showUploadButtons = false;
    this.showTrainButton = false;
    this.domSync.unbindListeners();
    this.domSync.cancelPositionSync();
  }

  @action
  next = () => {
    if (this.currentStep?.finalAction && this.showUploadButtons) {
      this.startUploadAndGenerate();
      return;
    }

    if (this.currentStepIndex < this.steps.length - 1) {
      this.currentStepIndex += 1;
      this.saveProgress();
      this.domSync.schedulePositionSync();
    } else {
      this.complete();
    }
  }

  @action
  back = () => {
    this.currentStepIndex = Math.max(0, this.currentStepIndex - 1);
    this.saveProgress();
    this.domSync.schedulePositionSync();
  }

  @action
  syncPosition = () => {
    const rect = getTargetRect(this.currentStep?.targetId);
    this.targetRect = rect;
    this.coachPosition = getCoachPosition(rect);
  }

  private startUploadAndGenerate() {
    const { uploadRoute } = this;
    const { teamId } = this;
    this.complete();

    if (teamId)
      this.store.uploadGuidedTraining.open({ teamId });

    if (uploadRoute)
      this.store.goTo(uploadRoute);
  }

  private saveProgress() {
    if (!this.teamId) return;

    this.store.storage.setLocal(this.progressStorageKey, {
      teamId: this.teamId,
      currentStepIndex: this.currentStepIndex,
      showUploadButtons: this.showUploadButtons,
      showTrainButton: this.showTrainButton,
    }, true);
  }

  private loadProgress(): Progress | null {
    return this.store.storage.getLocal<Progress>(this.progressStorageKey, true);
  }

  private handleKeyDown = (evt: KeyboardEvent) => {
    if (evt.key === 'Escape')
      this.pause();
  }
}
